"use client";
import React, { useState, useRef, useEffect } from "react";

interface CustomSelectProps {
  onChange(arg0: string | undefined): void;
  selected: {
    id?: string;
    icon?: string;
    text: string;
  };
  options: {
    id: string;
    icon?: string;
    text: string;
  }[];
  darkMode?: boolean;
  type: string;
}

export default function CustomSelect({
  onChange,
  selected,
  options,
  darkMode = true,
  type,
}: CustomSelectProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleOptionClick = (id: string | undefined) => {
    onChange(id);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const buttonClasses = `flex justify-between items-center w-full border-2 px-3 py-2 text-base rounded-lg h-[50px] ${
    darkMode ? "bg-black text-white border-[#444]" : "bg-white text-black"
  }`;

  const dropdownClasses = `absolute z-10 bottom-full mb-1 mt-1 w-full rounded shadow-lg overflow-hidden ${
    darkMode ? "bg-[rgb(47,47,47)] text-white" : "bg-white text-black"
  }`;

  const optionClasses = `flex items-center gap-2 px-3 py-2 cursor-pointer hover:text-primary`;

  return (
    <div className={`relative ${darkMode ? "mt-5" : ""} w-full`} ref={containerRef}>
      <button
        data-testid={`custom-select-${type}`}
        onClick={toggleDropdown}
        className={buttonClasses}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : "false"}
      >
        <div className="flex items-center gap-2">
          {selected.icon && (
            <img
              src={selected.icon}
              alt={selected.id || "flag"}
              width="17"
              height="11"
              loading="lazy"
            />
          )}
          {selected.text}
        </div>
        <svg
          className={`h-4 w-4 ${isOpen ? "rotate-[135rad]" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <ul className={dropdownClasses} role="menu">
          {options.map((item) => (
            <li
              key={item.id}
              data-testid={`custom-select-${type}-item-${item.id}`}
              onClick={() => handleOptionClick(item.id)}
              className={optionClasses}
              role="menuitem"
            >
              {item.icon && (
                <img
                  src={item.icon}
                  alt={item.id}
                  width="17"
                  height="11"
                  loading="lazy"
                  className="pr-2"
                />
              )}
              {item.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
